import { Box, FormHelperText, InputLabel, Typography } from "@mui/material";
import { useRCAMainStyles } from "../../RCA.styles";
import { useTranslation } from "react-i18next";
import { IRCAStatusDropdown } from "./RCAStatusDropdownInterfaces";
import RCAStatusBadge from "../RCAStatusBadge";
import { DefaultTranslationFieldsInTable, RCA_STATUSES } from "constants/constants";
import { StatusEntity } from "entities/RCA/StatusEntity/StatusEntity";
import { useEffect, useMemo, useState } from "react";
import { useStyles } from "./RCAStatusDropdown.styles";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { translateDefaultEntity } from "utilities/localesUtilities";

const RCAStatusDropdown = ({
    errors,
    values,
    touched,
    handleChange,
    value,
    disabled,
    fullWidth = true,
    hideLabel,
    asFilter,
    multiple,
    excludeStatuses = [],
    handleOnComponentMount,
    forActionPlan,
}: IRCAStatusDropdown) => {
    const mainClasses = useRCAMainStyles();
    const classes = useStyles();

    const { t, i18n } = useTranslation();
    const { statuses, loading } = StatusEntity.GetStatuses();
    const [startupStatusAssignedSetted, setStartupStatusAssignedSetted] = useState(false);

    const rcaStatusOptions = useMemo(
        () =>
            statuses
                .map((status) => ({
                    id: status.status_id,
                    name: translateDefaultEntity(i18n.language, status, "name", DefaultTranslationFieldsInTable.STATUS),
                    icon: <RCAStatusBadge status={status.status_id} />,
                }))
                .filter((status) => !excludeStatuses.includes(status.id))
                .filter((status) =>
                    forActionPlan
                        ? true
                        : status.id !== RCA_STATUSES.NEEDS_APPROVAL && status.id !== RCA_STATUSES.REJECTED,
                ),

        [excludeStatuses, forActionPlan, i18n.language, statuses],
    );

    const rcaStatusOptionsWithAll = useMemo(() => {
        return [...rcaStatusOptions];
    }, [rcaStatusOptions]);

    useEffect(() => {
        if (multiple && rcaStatusOptions.length > 0 && !startupStatusAssignedSetted) {
            if (handleOnComponentMount) handleOnComponentMount([...rcaStatusOptions.map((option) => option.id)]);
            setStartupStatusAssignedSetted(true);
        }
    }, [handleChange, multiple, startupStatusAssignedSetted, rcaStatusOptions, handleOnComponentMount]);

    if (multiple) {
        return (
            <Box className={`${fullWidth ? mainClasses.contentFormBox : ""}`}>
                {!hideLabel && (
                    <InputLabel id="outlined-statusId-label">
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.status_select.title")}
                        </Typography>
                    </InputLabel>
                )}

                <>
                    <NewMainSelect
                        disableMenuPropsStyles
                        multiple
                        className={classes.select}
                        options={asFilter ? rcaStatusOptionsWithAll : rcaStatusOptions || []}
                        name="statusId"
                        disabled={disabled || loading || statuses.length === 0}
                        value={value}
                        onChange={(selected) => {
                            handleChange(selected);
                        }}
                        placeholder={t("rca.status_select.please_select")}
                    />
                    {!!errors?.statusId && !!touched?.statusId && (
                        <FormHelperText>{t(errors?.statusId)}</FormHelperText>
                    )}
                </>
            </Box>
        );
    }

    return (
        <Box className={`${fullWidth ? mainClasses.contentFormBox : ""}`}>
            {!hideLabel && (
                <InputLabel id="outlined-statusId-label">
                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                        {t("rca.status_select.title")}
                    </Typography>
                </InputLabel>
            )}
            <>
                <NewMainSelect
                    options={asFilter ? rcaStatusOptionsWithAll : rcaStatusOptions || []}
                    name="statusId"
                    disabled={disabled || loading || statuses.length === 0}
                    value={values ? values.statusId : value}
                    onChange={handleChange}
                    placeholder={t("rca.status_select.please_select")}
                />
                {!!errors?.statusId && !!touched?.statusId && <FormHelperText>{t(errors?.statusId)}</FormHelperText>}
            </>
        </Box>
    );
};

export default RCAStatusDropdown;
